import type { Expr, Stmt } from '../ir.js';
import { CodeWriter } from '../writer.js';
import type { Code, GenContext } from './context.js';
import { ATOM, joinParts, MISSING, numberLiteral, stringLiteral, wrap } from './context.js';

const P = { or: 1, and: 2, not: 3, cmp: 4, add: 5, mul: 6, unary: 7 };

const CMP: Record<string, string> = {
  '==': '=',
  '!=': '≠',
  '<': '<',
  '<=': '≤',
  '>': '>',
  '>=': '≥',
};

/**
 * Romanian Bacalaureat-style pseudocod (docs/pseudocod.md): `citește`, `scrie`, `←`,
 * `┌dacă … atunci … └■`, `┌cât timp … execută … └■`, `┌pentru i ← a, b execută … └■`,
 * `[a / b]` for the integer part, `%` for the remainder.
 */
export function generatePseudo(ctx: GenContext): CodeWriter {
  const w = new CodeWriter('│ ');

  const expr = (e: Expr): Code => {
    switch (e.k) {
      case 'num':
        return { code: numberLiteral(e.value), prec: e.value < 0 ? P.unary : ATOM };
      case 'text':
        return { code: stringLiteral(e.value), prec: ATOM };
      case 'bool':
        return { code: e.value ? 'adevărat' : 'fals', prec: ATOM };
      case 'var':
        return { code: ctx.id(e.name), prec: ATOM };
      case 'read':
        return { code: MISSING, prec: ATOM };
      case 'arith': {
        if (e.op === 'idiv') {
          return { code: `[${expr(e.left).code} / ${wrap(expr(e.right), P.mul + 1)}]`, prec: ATOM };
        }
        const op = e.op === 'mod' ? '%' : e.op;
        const prec = op === '+' || op === '-' ? P.add : P.mul;
        return {
          code: `${wrap(expr(e.left), prec)} ${op} ${wrap(expr(e.right), prec + 1)}`,
          prec,
        };
      }
      case 'compare':
        return {
          code: `${wrap(expr(e.left), P.add)} ${CMP[e.op] ?? e.op} ${wrap(expr(e.right), P.add)}`,
          prec: P.cmp,
        };
      case 'logic': {
        const prec = e.op === 'and' ? P.and : P.or;
        const op = e.op === 'and' ? 'și' : 'sau';
        return {
          // Nested and/or always get parentheses: clearer, and g++ -Wall asks for them.
          code: `${wrap(expr(e.left), P.and + 1)} ${op} ${wrap(expr(e.right), P.and + 1)}`,
          prec,
        };
      }
      case 'not':
        return { code: `not ${wrap(expr(e.arg), P.add)}`, prec: P.not };
      case 'join':
        return {
          code: joinParts(e)
            .map((p) => wrap(expr(p), P.add + 1))
            .join(' + '),
          prec: P.add,
        };
      case 'length':
        return { code: `lungime(${expr(e.arg).code})`, prec: ATOM };
      case 'code':
        return { code: `cod(${expr(e.arg).code})`, prec: ATOM };
      case 'chr':
        return { code: `caracter(${expr(e.arg).code})`, prec: ATOM };
      case 'case':
        return {
          code: `${e.upper ? 'majusculă' : 'minusculă'}(${expr(e.arg).code})`,
          prec: ATOM,
        };
      case 'find':
        return { code: `poziție(${expr(e.needle).code}, ${expr(e.hay).code})`, prec: ATOM };
      case 'substr':
        return {
          code: `subșir(${expr(e.arg).code}, ${expr(e.from).code}, ${expr(e.len).code})`,
          prec: ATOM,
        };
      case 'at': {
        const idx = `[${expr(e.index).code}]${e.index2 ? `[${expr(e.index2).code}]` : ''}`;
        return { code: `${ctx.id(e.name)}${idx}`, prec: ATOM };
      }
      case 'size':
        return {
          code: e.dim === 2 ? `coloane(${ctx.id(e.name)})` : `lungime(${ctx.id(e.name)})`,
          prec: ATOM,
        };
      case 'missing':
        return { code: MISSING, prec: ATOM };
    }
  };

  const e = (x: Expr) => expr(x).code;

  const block = (body: readonly Stmt[]) => w.indented(() => body.forEach(stmt));

  const closing = () => w.line('└■');

  /** if / else-if chains become nested `dacă` structures, as on paper. */
  const ifChain = (s: Stmt & { k: 'if' }, from: number): void => {
    const b = s.branches[from]!;
    const line = w.line(`┌dacă ${e(b.cond)} atunci`);
    w.mapExpr(b.cond, line);
    block(b.body);
    const hasMore = from + 1 < s.branches.length;
    if (hasMore || s.orElse) {
      w.line('│altfel');
      w.indented(() => {
        if (hasMore) ifChain(s, from + 1);
        else s.orElse!.forEach(stmt);
      });
    }
    closing();
  };

  const stmt = (s: Stmt): void => {
    switch (s.k) {
      case 'set':
        w.stmt(s, () =>
          w.line(
            s.value.k === 'read'
              ? `citește ${ctx.id(s.name)}`
              : `${ctx.id(s.name)} ← ${e(s.value)}`,
          ),
        );
        break;
      case 'change': {
        const d = s.delta;
        const name = ctx.id(s.name);
        const text =
          d.k === 'num' && d.value < 0
            ? `${name} ← ${name} - ${numberLiteral(-d.value)}`
            : `${name} ← ${name} + ${wrap(expr(d), P.add + 1)}`;
        w.stmt(s, () => w.line(text));
        break;
      }
      case 'print': {
        const parts = (s.value.k === 'join' ? joinParts(s.value) : [s.value]).map(e);
        if (s.end === 'space') parts.push("' '");
        w.stmt(s, () => w.line(`scrie ${parts.join(', ')}`));
        break;
      }
      case 'if': {
        // The header is the first `dacă` line; later branches map their conditions themselves.
        const first = s.branches[0]!;
        w.stmt(
          s,
          () => w.line(`┌dacă ${e(first.cond)} atunci`),
          () => {
            block(first.body);
            const rest = { ...s, branches: s.branches.slice(1) };
            if (rest.branches.length > 0 || s.orElse) {
              w.line('│altfel');
              w.indented(() => {
                if (rest.branches.length > 0) ifChain(rest, 0);
                else s.orElse!.forEach(stmt);
              });
            }
            closing();
          },
        );
        break;
      }
      case 'repeat': {
        const k = ctx.counter(s.id);
        w.stmt(
          s,
          () => w.line(`┌pentru ${k} ← 1, ${e(s.times)} execută`),
          () => {
            block(s.body);
            closing();
          },
        );
        break;
      }
      case 'while':
        w.stmt(
          s,
          () => w.line(`┌cât timp ${e(s.cond)} execută`),
          () => {
            block(s.body);
            closing();
          },
        );
        break;
      case 'for': {
        const step = s.down ? ', -1' : '';
        w.stmt(
          s,
          () => w.line(`┌pentru ${ctx.id(s.name)} ← ${e(s.from)}, ${e(s.to)}${step} execută`),
          () => {
            block(s.body);
            closing();
          },
        );
        break;
      }
      case 'newArray': {
        // Bac pseudocode has no declarations; the size line says what the list holds.
        const size = (d: Expr) => (s.base === 1 ? `${wrap(expr(d), P.add)} + 1` : e(d));
        const [rows, cols] = s.dims;
        const text = cols
          ? `${ctx.id(s.name)} ← tablou de ${size(rows)} × ${size(cols)}`
          : `${ctx.id(s.name)} ← tablou de ${size(rows)}`;
        w.stmt(s, () => w.line(text));
        break;
      }
      case 'setAt': {
        const place = `${ctx.id(s.name)}[${e(s.index)}]${s.index2 ? `[${e(s.index2)}]` : ''}`;
        const line = s.value.k === 'read' ? `citește ${place}` : `${place} ← ${e(s.value)}`;
        w.stmt(s, () => w.line(line));
        break;
      }
      case 'break':
        w.stmt(s, () => w.line('ieși din buclă'));
        break;
    }
  };

  ctx.program.body.forEach(stmt);
  w.mapAll(ctx.program.startId);
  return w;
}
